import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Flex, Typography } from 'antd';

import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import messages from '../../../constants/messages';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchStockTypes } from '../../../redux/actions/animal';
import { fetchBreeds } from '../../../redux/actions/breed';
import { fetchFarms } from '../../../redux/actions/farm';
import { fetchLabels } from '../../../redux/actions/label';
import { fetchMeasurementTypes } from '../../../redux/actions/measurement';

import CancelSaveButtons from '../../../components/CancelSaveButtons';
import LoadingBar from '../../../components/LoadingBar';
import { errorToastHandler, warnToastHandler } from '../../../components/action_notifier';
import AnimalCard from '../../../components/animals/animalCard';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';

const { Title } = Typography;

function NewAnimal({ history, match }) {
  const dispatch = useDispatch();

  const { subscription } = useSelector(state => state.user);
  const { list: farms } = useSelector(state => state.farm);

  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    animal: {},
    animal_edit: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    getLabels();
  }, [farms]);

  async function fetchData() {
    try {
      await Promise.all([getTypes(), getFarms(), getBreeds(), getMeasurementTypes()]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    } finally {
      setLoading(false);
    }
  }

  async function getMeasurementTypes() {
    await dispatch(fetchMeasurementTypes()).unwrap();
  }

  async function getFarms() {
    await dispatch(fetchFarms({ with_details: true })).unwrap();
  }

  async function getLabels() {
    if (!farms?.length) return;

    try {
      await dispatch(fetchLabels({ farm_ids: farms.map(f => f.id) })).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getBreeds() {
    await dispatch(fetchBreeds());
  }

  async function getTypes() {
    await dispatch(fetchStockTypes()).unwrap();
  }

  function isLivestockDataValid(livestockData) {
    return (
      !isEmpty(livestockData) &&
      (livestockData.eartag_management_id ||
        livestockData.eartag_management_id !== '' ||
        livestockData.farm_id ||
        livestockData.farm_id !== '' ||
        livestockData.specie ||
        livestockData.specie !== '' ||
        livestockData.sex ||
        livestockData.sex !== '')
    );
  }

  function formPayload(livestockData) {
    const payload = {};

    payload.geofence_ids = livestockData.geofence_ids ?? [];

    if (state.animal_edit.photo) {
      payload.photo = state.animal_edit.photo;
    }

    return { ...livestockData, ...payload };
  }

  async function addLivestock() {
    try {
      const livestockData = await formRef.current?.validateFields();

      if (!isLivestockDataValid(livestockData)) {
        errorToastHandler(messages.FILL_MANDATORY_FIELDS);
        return false;
      } else {
        const payload = formPayload(livestockData);
        const response = await axios.post('animals', payload);

        if (response.status === 200 && response.data.id) {
          setIsFormDirty(false);
          history.push(`/animals/${response.data.id}`);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const setAnimalPhoto = photo => {
    setState(prevState => ({ ...prevState, animal_edit: { ...prevState.animal_edit, photo } }));
  };

  const cancelChanges = useCallback(async () => {
    history.push('/animals');
  }, []);

  const { NavigationPrompt, modalProps, nextLocation, confirmed } = useUnsavedChangesPrompt({
    when: isFormDirty,
    onSaveAndLeave: async () => {
      if (submittable) {
        await addLivestock();

        return true;
      } else {
        warnToastHandler(messages.DATA_REQUIRED);
        formRef.current?.validateFields();

        return false;
      }
    },
    onDiscardAndLeave: () => {
      formRef.current?.resetFields();
      setIsFormDirty(false);
    }
  });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(nextLocation.pathname);
    }
  }, [isFormDirty, nextLocation, history]);

  return (
    <>
      {NavigationPrompt}
      <Flex gap="small" vertical={false} justify="space-between" wrap>
        <Flex vertical={true}>
          <Title level={4}>Livestock {state.animal.identifier || state.animal.eartag_official_id}</Title>

          <AppBreadcrumb
            items={[
              {
                title: <Link to="/animals">List of Livestock</Link>
              },
              {
                title: 'Create New Livestock'
              }
            ]}
          />
        </Flex>
        <Flex>
          <CancelSaveButtons disabled={!submittable} handleCancel={cancelChanges} handleSave={addLivestock} />
        </Flex>
      </Flex>

      {loading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <AnimalCard
          className="mb-2 p-2"
          page="newAnimal"
          actionType={match.params.type}
          animal={state.animal_edit}
          editMode={true}
          isNew={true}
          formRef={formRef}
          subscription={subscription}
          onChange={setIsFormDirty}
          onPhotoChange={setAnimalPhoto}
          setSubmittable={setSubmittable}
        />
      )}

      <Flex justify="end" className="mb-2">
        <CancelSaveButtons disabled={!submittable} handleCancel={cancelChanges} handleSave={addLivestock} />
      </Flex>

      <UnsavedChangesModal
        isOpen={modalProps.visible}
        handleCancel={modalProps.onCancel}
        handleConfirm={modalProps.onSave}
        handleDiscard={modalProps.onDiscard}
      />
    </>
  );
}

export default NewAnimal;
